import { Loader2 } from "lucide-react";
import { toneFor } from "@/lib/constants";

export const inputCls =
  "w-full h-11 rounded-lg border border-slate-300 bg-white px-3.5 text-[15px] text-slate-900 placeholder:text-slate-400 transition-[border-color,box-shadow] focus:outline-none focus:ring-4 focus:ring-emerald-600/15 focus:border-emerald-600 disabled:bg-slate-50";
export const textareaCls = inputCls.replace("h-11", "min-h-[96px] py-2.5");
export const smInput = "h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600";

export function Field({ label, error, hint, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-semibold text-slate-800">
        {label} {required && <span className="text-emerald-700">*</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
      {error && <span className="mt-1 block text-xs font-medium text-rose-600" role="alert">{error}</span>}
    </label>
  );
}

const variants = {
  primary: "bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm shadow-emerald-900/20",
  dark: "bg-slate-900 text-white hover:bg-slate-800",
  outline: "border border-slate-300 bg-white text-slate-800 hover:border-emerald-600 hover:text-emerald-800",
  ghost: "text-slate-700 hover:bg-slate-100",
  danger: "bg-rose-600 text-white hover:bg-rose-700",
  light: "bg-white text-slate-900 hover:bg-emerald-50",
};
const sizes = { sm: "h-9 px-3.5 text-sm", md: "h-11 px-5 text-[15px]", lg: "h-14 px-7 text-base" };

export function Btn({ variant = "primary", size = "md", loading, className = "", children, ...p }) {
  return (
    <button
      {...p}
      disabled={loading || p.disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function StatusBadge({ value, text, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${toneFor(value)}`}>
      {text || value}
    </span>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export const Card = ({ className = "", children, ...p }) => (
  <div {...p} className={`rounded-xl border border-slate-200 bg-white ${className}`}>{children}</div>
);

export const Empty = ({ children }) => <div className="py-10 text-center text-sm text-slate-500">{children}</div>;
